import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { fmtINR } from "../lib/constants";
import { ArrowLeft, FileText, CheckCircle2, Circle, Clock, Upload, Plus, BarChart3, Package, CalendarClock, AlertTriangle, Save, Users as UsersIcon, Download, Pencil, Mail } from "lucide-react";
import { toast } from "sonner";
import { Modal, Input, Select } from "./Leads";
import KYCTab from "./KYCTab";
import CommsTab from "./CommsTab";

const TABS = ["Overview", "Charter", "Timeline", "Requirements", "Folders", "Checklist", "KYC", "Comms", "Comparative", "Compliance", "Monitoring"];

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [charter, setCharter] = useState(null);
  const [docs, setDocs] = useState([]);
  const [comparative, setComparative] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [monitoring, setMonitoring] = useState([]);
  const [users, setUsers] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [remarks, setRemarks] = useState([]);
  const [tab, setTab] = useState("Overview");
  const [docOpen, setDocOpen] = useState(false);
  const [compOpen, setCompOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [remarkText, setRemarkText] = useState("");
  const [remarkVisible, setRemarkVisible] = useState(true);
  const [emailingUpdate, setEmailingUpdate] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = async () => {
    let p;
    try {
      p = await api.get(`/projects/${id}`);
    } catch {
      setNotFound(true);
      return;
    }
    const [c, d, cmp, co, mo, u, ms, rem] = await Promise.all([
      api.get(`/projects/${id}/charter`),
      api.get(`/projects/${id}/documents`),
      api.get(`/projects/${id}/comparative`),
      api.get(`/compliance`, { params: { project_id: id } }),
      api.get(`/monitoring`, { params: { project_id: id } }),
      api.get(`/users`),
      api.get(`/projects/${id}/milestones`).catch(() => ({ data: [] })),
      api.get(`/projects/${id}/remarks`).catch(() => ({ data: [] })),
    ]);
    setProject(p.data);
    try {
      const rec = JSON.parse(localStorage.getItem("recent_projects") || "[]").filter((r) => r.id !== p.data.id);
      rec.unshift({ id: p.data.id, name: p.data.project_name, client: p.data.client_name });
      localStorage.setItem("recent_projects", JSON.stringify(rec.slice(0, 8)));
    } catch { /* ignore */ }
    setCharter(c.data);
    setDocs(d.data);
    setComparative(cmp.data);
    setCompliance(co.data);
    setMonitoring(mo.data);
    setUsers(u.data);
    setMilestones(ms.data || []);
    setRemarks(rem.data || []);
  };

  const postRemark = async () => {
    const txt = remarkText.trim();
    if (!txt) return;
    try {
      await api.post(`/projects/${id}/remarks`, { project_id: id, text: txt, client_visible: remarkVisible, type: "Update" });
      setRemarkText("");
      toast.success(remarkVisible ? "Remark posted — visible to client" : "Internal remark saved");
      load();
    } catch { toast.error("Failed"); }
  };
  useEffect(() => { load(); }, [id]);

  if (notFound) {
    return (
      <div className="text-center py-20" data-testid="project-not-found">
        <div className="text-lg font-semibold text-stone-800">Project not found</div>
        <div className="text-sm text-stone-500 mt-1">It may have been deleted or you don't have access to it.</div>
        <Link to="/projects" className="inline-block mt-4 text-sm bg-stone-900 text-white px-4 py-2 rounded-lg">← Back to Projects</Link>
      </div>
    );
  }
  if (!project) return <div className="text-stone-500">Loading...</div>;

  const stages = project.stages || [];
  const idx = stages.indexOf(project.current_stage);
  const userNameById = (uid) => users.find((u) => u.id === uid)?.name || "—";

  const advance = async () => {
    await api.put(`/projects/${id}/advance-stage`);
    toast.success("Stage advanced");
    load();
  };

  const emailClientUpdate = async () => {
    setEmailingUpdate(true);
    try {
      const { data } = await api.post(`/emails/project-update/${id}`);
      toast.success(`Status update emailed to ${data.to}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
    finally { setEmailingUpdate(false); }
  };

  const updateDoc = async (did, status) => {
    await api.put(`/documents/${did}`, { status });
    load();
  };

  const docProgress = docs.length
    ? Math.round((docs.filter((d) => d.status === "Approved").length / docs.length) * 100)
    : 0;

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <Link to="/projects" className="text-sm text-stone-500 hover:text-stone-900 flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to projects
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full font-medium">{project.service_category}</span>
              <span className="text-[10px] uppercase tracking-wider bg-stone-900 text-white px-2 py-1 rounded-full font-medium">{project.service_type}</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "Bricolage Grotesque" }}>{project.project_name}</h1>
            <p className="text-stone-500 mt-1">{project.client_name} · {project.project_location}</p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-stone-500">Contract</div>
            <div className="text-2xl font-bold text-stone-900">{fmtINR(project.contract_value)}</div>
          </div>
        </div>
      </div>

      {/* Stage tracker */}
      <div className="bg-white border border-stone-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-semibold">Submission Workflow</div>
          <div className="flex items-center gap-2">
            <button onClick={emailClientUpdate} disabled={emailingUpdate} data-testid="email-client-update-btn" className="text-xs bg-violet-600 hover:bg-violet-700 text-white px-3 py-1.5 rounded-md font-medium transition flex items-center gap-1.5 disabled:opacity-60">
              <Mail className="w-3 h-3" /> {emailingUpdate ? "Sending..." : "Email Client Update"}
            </button>
            <button onClick={advance} data-testid="advance-stage-btn" className="text-xs bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-1.5 rounded-md font-medium transition">
              Advance Stage →
            </button>
          </div>
        </div>
        <div className="relative">
          <div className="absolute top-3 left-3 right-3 h-0.5 bg-stone-200" />
          <div className="absolute top-3 left-3 h-0.5 bg-emerald-600 transition-all" style={{ width: stages.length ? `calc(${(idx / Math.max(stages.length - 1, 1)) * 100}% - 12px)` : 0 }} />
          <div className="relative flex justify-between">
            {stages.map((s, i) => (
              <div key={s} className="flex flex-col items-center gap-2 flex-1">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition ${
                  i < idx ? "bg-emerald-600 border-emerald-600 text-white"
                  : i === idx ? "bg-amber-500 border-amber-500 text-white ring-4 ring-amber-100"
                  : "bg-white border-stone-300 text-stone-400"
                }`}>
                  {i < idx ? <CheckCircle2 className="w-3.5 h-3.5" /> : i === idx ? <Clock className="w-3.5 h-3.5" /> : <Circle className="w-3 h-3" />}
                </div>
                <div className={`text-[10px] text-center leading-tight px-1 ${i <= idx ? "text-stone-900 font-medium" : "text-stone-500"}`}>{s}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-stone-200">
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${t}`}
              className={`px-4 py-2.5 text-sm font-medium transition border-b-2 -mb-px ${
                tab === t ? "border-emerald-700 text-emerald-800" : "border-transparent text-stone-500 hover:text-stone-900"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {tab === "Overview" && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <button onClick={() => setEditOpen(true)} data-testid="edit-project-btn"
              className="flex items-center gap-2 bg-[#0F2A4A] hover:bg-[#0F2A4A]/90 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
              <Pencil className="w-4 h-4" /> Edit Project
            </button>
          </div>
          <div className="bg-white border border-stone-200 rounded-xl p-6" data-testid="project-details-card">
            <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-semibold mb-4">Project Details</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 text-sm">
              <Row label="Project ID" value={project.project_id_external} />
              <Row label="Status" value={project.status} />
              <Row label="Client" value={project.client_name} />
              <Row label="Location" value={project.project_location} />
              <Row label="Start Date" value={project.start_date} />
              <Row label="Target Completion" value={project.target_completion_date} />
              <Row label="Estimated EC Date" value={project.estimated_ec_date} />
              <Row label="Survey Number" value={project.survey_number} />
              <Row label="Plot Area" value={project.plot_area} />
              <Row label="Built-up Area" value={project.builtup_area} />
              <Row label="EC Category" value={project.ec_category} />
              <Row label="WO Number" value={project.wo_number} />
            </div>
          </div>
          {project.product_name && (
            <div className="bg-white border border-emerald-200 rounded-xl p-6" data-testid="linked-product-card">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <ProductIcon />
                </div>
                <div className="flex-1">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-semibold">Linked PTESPL Product</div>
                  <div className="font-semibold text-lg text-stone-900 mt-0.5" style={{ fontFamily: "Bricolage Grotesque" }}>{project.product_name}</div>
                  {project.product_scope && (
                    <div className="mt-3 text-sm text-stone-700 whitespace-pre-line leading-relaxed">{project.product_scope}</div>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-stone-200 rounded-xl p-6">
              <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-semibold mb-4">Team</div>
              <dl className="space-y-3 text-sm">
                <Row label="Project Lead" value={userNameById(project.project_lead)} />
                <Row label="Compliance Executive" value={userNameById(project.compliance_executive)} />
                <Row label="Monitoring Executive" value={userNameById(project.monitoring_executive)} />
                <Row label="HOD" value={userNameById(project.hod)} />
              </dl>
            </div>
            <div className="bg-white border border-stone-200 rounded-xl p-6">
              <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-semibold mb-4">Document progress</div>
              <div className="flex items-center gap-4">
                <div className="relative w-20 h-20">
                  <svg className="w-20 h-20 -rotate-90">
                    <circle cx="40" cy="40" r="32" stroke="#e7e5e4" strokeWidth="6" fill="none" />
                    <circle cx="40" cy="40" r="32" stroke="#047857" strokeWidth="6" fill="none"
                      strokeDasharray={`${(docProgress / 100) * 201} 201`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center text-lg font-bold text-stone-900">{docProgress}%</div>
                </div>
                <div>
                  <div className="text-sm text-stone-700">{docs.filter((d) => d.status === "Approved").length} of {docs.length} documents approved</div>
                  <div className="text-xs text-stone-500 mt-1">{docs.filter((d) => d.status === "Pending").length} pending · {docs.filter((d) => d.status === "Uploaded").length} in review</div>
                </div>
              </div>
            </div>
          </div>

          {/* TEAM REMARKS / COMMENTS */}
          <div className="bg-white border border-stone-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-semibold">Team Remarks · Latest Comments</div>
              <span className="text-[10px] text-stone-400">Client sees only "Visible" remarks</span>
            </div>
            <div className="flex flex-col gap-2 mb-4">
              <textarea value={remarkText} onChange={(e) => setRemarkText(e.target.value)} rows={2} placeholder="Add an update for this project..."
                data-testid="remark-input"
                className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-[#72BE44]" />
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="flex items-center gap-2 text-xs text-stone-600">
                  <input type="checkbox" checked={remarkVisible} onChange={(e) => setRemarkVisible(e.target.checked)} data-testid="remark-visible" />
                  Visible to client portal
                </label>
                <button onClick={postRemark} disabled={!remarkText.trim()} data-testid="remark-submit"
                  className="text-xs flex items-center gap-1.5 bg-[#72BE44] disabled:opacity-40 text-white px-4 py-2 rounded-lg font-medium">
                  Post Remark
                </button>
              </div>
            </div>
            <div className="space-y-2.5">
              {remarks.length === 0 && <div className="text-sm text-stone-500">No remarks yet — be the first to post an update.</div>}
              {remarks.map((r) => (
                <div key={r.id} data-testid={`remark-row-${r.id}`} className={`border-l-2 pl-3 py-1.5 ${r.client_visible ? "border-[#72BE44]" : "border-stone-300"}`}>
                  <div className="text-sm text-stone-800 whitespace-pre-wrap">{r.text}</div>
                  <div className="text-[10px] text-stone-500 mt-1 uppercase tracking-wider">
                    {r.author_name || "Team"} · {(r.created_at || "").slice(0, 16).replace("T", " ")} ·
                    <span className={`ml-1 ${r.client_visible ? "text-[#72BE44]" : "text-stone-400"}`}>
                      {r.client_visible ? "Visible to client" : "Internal only"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "Charter" && charter && (
        <CharterView charter={charter} project={project} users={users} onSaved={load} />
      )}

      {tab === "Timeline" && (
        <TimelineView projectId={id} milestones={milestones} users={users} onReload={load} />
      )}

      {tab === "Requirements" && <RequirementsView projectId={id} />}

      {tab === "Folders" && <FoldersView projectId={id} />}

      {tab === "Checklist" && (
        <div className="bg-white border border-stone-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-base font-semibold text-stone-900">Dynamic Document Checklist</div>
              <div className="text-xs text-stone-500 mt-0.5">Auto-loaded for {project.service_type}</div>
            </div>
            <button onClick={() => setDocOpen(true)} data-testid="add-doc-btn" className="text-xs bg-stone-900 text-white px-3 py-2 rounded-lg flex items-center gap-1.5">
              <Plus className="w-3 h-3" /> Add Document
            </button>
          </div>
          <div className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center gap-3 p-3 border border-stone-100 rounded-lg hover:bg-stone-50">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  d.status === "Approved" ? "bg-emerald-50 text-emerald-700"
                  : d.status === "Uploaded" ? "bg-sky-50 text-sky-700"
                  : "bg-stone-100 text-stone-500"
                }`}>
                  {d.status === "Approved" ? <CheckCircle2 className="w-4 h-4" /> : d.status === "Uploaded" ? <Upload className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-stone-900">{d.name}</div>
                  <div className="text-xs text-stone-500">{d.category}</div>
                </div>
                <select
                  value={d.status}
                  onChange={(e) => updateDoc(d.id, e.target.value)}
                  data-testid={`doc-status-${d.id}`}
                  className="text-xs px-3 py-1.5 bg-white border border-stone-200 rounded-md focus:outline-none focus:border-emerald-700"
                >
                  <option>Pending</option><option>Uploaded</option><option>Approved</option>
                </select>
              </div>
            ))}
            {docs.length === 0 && <div className="text-sm text-stone-500 text-center py-8">No documents</div>}
          </div>
        </div>
      )}

      {tab === "Comparative" && (
        <ComparativeStatement projectId={id} items={comparative} onAdd={load} />
      )}

      {tab === "KYC" && <KYCTab projectId={id} />}

      {tab === "Comms" && <CommsTab projectId={id} />}

      {tab === "Compliance" && (
        <ComplianceTab projectId={id} items={compliance} onUpdate={load} />
      )}

      {tab === "Monitoring" && (
        <MonitoringTab projectId={id} items={monitoring} onUpdate={load} />
      )}

      {docOpen && (
        <Modal title="Add Document" onClose={() => setDocOpen(false)}>
          <AddDoc projectId={id} onSaved={() => { setDocOpen(false); load(); }} category={project.service_type} />
        </Modal>
      )}

      {editOpen && (
        <Modal title="Edit Project" onClose={() => setEditOpen(false)}>
          <EditProject project={project} users={users} onSaved={() => { setEditOpen(false); load(); }} />
        </Modal>
      )}
    </div>
  );
}

function EditProject({ project, users, onSaved }) {
  const teamOpts = [{ value: "", label: "—" }, ...users.filter((u) => u.role !== "client").map((u) => ({ value: u.id, label: u.name }))];
  const [form, setForm] = useState({
    project_name: project.project_name || "", client_name: project.client_name || "",
    project_location: project.project_location || "", contract_value: project.contract_value || 0,
    status: project.status || "Active",
    survey_number: project.survey_number || "", plot_area: project.plot_area || "",
    builtup_area: project.builtup_area || "", ec_category: project.ec_category || "",
    project_lead: project.project_lead || "", compliance_executive: project.compliance_executive || "",
    monitoring_executive: project.monitoring_executive || "", hod: project.hod || "",
    start_date: project.start_date || "", target_completion_date: project.target_completion_date || "",
  });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/projects/${project.id}`, { ...form, contract_value: Number(form.contract_value) });
      toast.success("Project updated");
      onSaved();
    } catch { toast.error("Update failed"); }
  };
  return (
    <form onSubmit={save} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Input label="Project Name" value={form.project_name} onChange={(v) => setForm({ ...form, project_name: v })} required testid="edit-proj-name" />
        <Input label="Client Name" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} required testid="edit-proj-client" />
      </div>
      <Input label="Location" value={form.project_location} onChange={(v) => setForm({ ...form, project_location: v })} testid="edit-proj-location" />
      <div className="grid grid-cols-2 gap-3">
        <Input label="Contract Value (₹)" type="number" value={form.contract_value} onChange={(v) => setForm({ ...form, contract_value: v })} testid="edit-proj-value" />
        <Select label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={["Active", "On Hold", "Completed", "Cancelled"]} testid="edit-proj-status" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input label="Survey Number" value={form.survey_number} onChange={(v) => setForm({ ...form, survey_number: v })} testid="edit-proj-survey" />
        <Input label="EC Category (A / B1 / B2)" value={form.ec_category} onChange={(v) => setForm({ ...form, ec_category: v })} testid="edit-proj-ec-cat" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input label="Plot Area (sqm)" value={form.plot_area} onChange={(v) => setForm({ ...form, plot_area: v })} testid="edit-proj-plot" />
        <Input label="Built-up Area (sqm)" value={form.builtup_area} onChange={(v) => setForm({ ...form, builtup_area: v })} testid="edit-proj-bua" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Select label="Project Lead" value={form.project_lead} onChange={(v) => setForm({ ...form, project_lead: v })} options={teamOpts} testid="edit-proj-lead" />
        <Select label="HOD" value={form.hod} onChange={(v) => setForm({ ...form, hod: v })} options={teamOpts} testid="edit-proj-hod" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Select label="Compliance Executive" value={form.compliance_executive} onChange={(v) => setForm({ ...form, compliance_executive: v })} options={teamOpts} testid="edit-proj-comp-exec" />
        <Select label="Monitoring Executive" value={form.monitoring_executive} onChange={(v) => setForm({ ...form, monitoring_executive: v })} options={teamOpts} testid="edit-proj-mon-exec" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input label="Start Date" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testid="edit-proj-start" />
        <Input label="Target Completion Date" type="date" value={form.target_completion_date} onChange={(v) => setForm({ ...form, target_completion_date: v })} testid="edit-proj-target" />
      </div>
      <button type="submit" data-testid="edit-proj-submit" className="w-full bg-[#72BE44] hover:bg-[#5da636] text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2">
        <Save className="w-4 h-4" /> Save Changes
      </button>
    </form>
  );
}

function ProductIcon() {
  return <Package className="w-5 h-5 text-emerald-700" strokeWidth={1.8} />;
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-baseline gap-3 py-1 border-b border-stone-100 last:border-b-0">
      <dt className="text-xs uppercase tracking-wider text-stone-500 capitalize">{label}</dt>
      <dd className="text-sm font-medium text-stone-900 text-right">{value || "—"}</dd>
    </div>
  );
}

function AddDoc({ projectId, onSaved, category }) {
  const [form, setForm] = useState({ name: "", category, status: "Pending" });
  const save = async (e) => {
    e.preventDefault();
    await api.post(`/projects/${projectId}/documents`, { ...form, project_id: projectId });
    onSaved();
  };
  return (
    <form onSubmit={save} className="space-y-3">
      <Input label="Document Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="add-doc-name" />
      <Input label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="add-doc-category" />
      <Select label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={["Pending", "Uploaded", "Approved"]} testid="add-doc-status" />
      <button type="submit" className="w-full bg-emerald-700 text-white py-2.5 rounded-lg font-medium" data-testid="add-doc-submit">Add Document</button>
    </form>
  );
}

function ComparativeStatement({ projectId, items, onAdd }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ particular: "", existing: "", proposed: "" });
  const ROWS = ["Plot Area (sqm)", "FSI", "BUA (sqm)", "Buildings", "Floors", "Height (m)", "Tenements", "Parking"];
  const save = async (e) => {
    e.preventDefault();
    await api.post(`/projects/${projectId}/comparative`, { ...form, project_id: projectId });
    onAdd();
    setOpen(false);
    setForm({ particular: "", existing: "", proposed: "" });
  };
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-base font-semibold text-stone-900">Comparative Statement</div>
          <div className="text-xs text-stone-500 mt-0.5">Existing EC vs Proposed Amendment</div>
        </div>
        <button onClick={() => setOpen(true)} data-testid="add-comparative-btn" className="text-xs bg-stone-900 text-white px-3 py-2 rounded-lg flex items-center gap-1.5">
          <Plus className="w-3 h-3" /> Add Row
        </button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-stone-50 text-xs uppercase tracking-wider text-stone-600">
          <tr>
            <th className="text-left px-4 py-3 font-medium">Particular</th>
            <th className="text-left px-4 py-3 font-medium">Existing EC</th>
            <th className="text-left px-4 py-3 font-medium">Proposed</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <>
              {ROWS.map((r) => (
                <tr key={r} className="border-t border-stone-100">
                  <td className="px-4 py-3 text-stone-800 font-medium">{r}</td>
                  <td className="px-4 py-3 text-stone-400 italic">Not provided</td>
                  <td className="px-4 py-3 text-stone-400 italic">Not provided</td>
                </tr>
              ))}
            </>
          )}
          {items.map((c) => (
            <tr key={c.id} className="border-t border-stone-100 hover:bg-stone-50">
              <td className="px-4 py-3 font-medium text-stone-900">{c.particular}</td>
              <td className="px-4 py-3 text-stone-700">{c.existing}</td>
              <td className="px-4 py-3 bg-emerald-50/40 text-stone-900 font-medium">{c.proposed}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {open && (
        <Modal title="Add Comparative Row" onClose={() => setOpen(false)}>
          <form onSubmit={save} className="space-y-3">
            <Input label="Particular" value={form.particular} onChange={(v) => setForm({ ...form, particular: v })} required testid="cmp-particular" />
            <Input label="Existing" value={form.existing} onChange={(v) => setForm({ ...form, existing: v })} testid="cmp-existing" />
            <Input label="Proposed" value={form.proposed} onChange={(v) => setForm({ ...form, proposed: v })} testid="cmp-proposed" />
            <button type="submit" data-testid="cmp-submit" className="w-full bg-emerald-700 text-white py-2.5 rounded-lg font-medium">Add</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function ComplianceTab({ projectId, items, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "PMR", title: "", due_date: "", status: "Pending" });
  const save = async (e) => {
    e.preventDefault();
    await api.post("/compliance", { ...form, project_id: projectId });
    setOpen(false); onUpdate();
  };
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="text-base font-semibold text-stone-900">Compliance Calendar</div>
        <button onClick={() => setOpen(true)} className="text-xs bg-stone-900 text-white px-3 py-2 rounded-lg flex items-center gap-1.5">
          <Plus className="w-3 h-3" /> Add Compliance
        </button>
      </div>
      <div className="space-y-2">
        {items.map((c) => (
          <div key={c.id} className="flex items-center gap-3 p-3 border border-stone-100 rounded-lg">
            <BarChart3 className="w-4 h-4 text-emerald-700" />
            <div className="flex-1">
              <div className="text-sm font-medium">{c.title}</div>
              <div className="text-xs text-stone-500">{c.type} · Due {c.due_date}</div>
            </div>
            <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-1 rounded-full font-medium">{c.status}</span>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-stone-500 text-center py-8">No compliance items</div>}
      </div>
      {open && (
        <Modal title="Add Compliance" onClose={() => setOpen(false)}>
          <form onSubmit={save} className="space-y-3">
            <Select label="Type" value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={["PMR", "Six Monthly", "Form V", "Monitoring", "EC Condition"]} testid="comp-type" />
            <Input label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testid="comp-title" />
            <Input label="Due Date" type="date" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} required testid="comp-due" />
            <button type="submit" data-testid="comp-submit" className="w-full bg-emerald-700 text-white py-2.5 rounded-lg font-medium">Add</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function MonitoringTab({ projectId, items, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ monitoring_type: "Air", scheduled_date: "", status: "Scheduled" });
  const save = async (e) => {
    e.preventDefault();
    await api.post("/monitoring", { ...form, project_id: projectId });
    setOpen(false); onUpdate();
  };
  const updateStatus = async (id, status) => {
    await api.put(`/monitoring/${id}`, { status });
    onUpdate();
  };
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="text-base font-semibold text-stone-900">Monitoring Schedule</div>
        <button onClick={() => setOpen(true)} className="text-xs bg-stone-900 text-white px-3 py-2 rounded-lg flex items-center gap-1.5">
          <Plus className="w-3 h-3" /> Schedule
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((m) => (
          <div key={m.id} className="border border-stone-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-stone-900">{m.monitoring_type}</div>
              <span className="text-[10px] uppercase tracking-wider bg-stone-100 text-stone-700 px-2 py-1 rounded-full">{m.status}</span>
            </div>
            <div className="text-xs text-stone-500 mb-3">Scheduled: {m.scheduled_date}</div>
            <select
              value={m.status}
              onChange={(e) => updateStatus(m.id, e.target.value)}
              className="text-xs px-2.5 py-1.5 w-full bg-stone-50 border border-stone-200 rounded-md focus:outline-none focus:border-emerald-700"
            >
              <option>Scheduled</option><option>Sample Collected</option><option>Report Received</option><option>Uploaded</option>
            </select>
          </div>
        ))}
      </div>
      {open && (
        <Modal title="Schedule Monitoring" onClose={() => setOpen(false)}>
          <form onSubmit={save} className="space-y-3">
            <Select label="Type" value={form.monitoring_type} onChange={(v) => setForm({ ...form, monitoring_type: v })} options={["Air", "Water", "Noise", "STP", "Waste"]} testid="mon-type" />
            <Input label="Scheduled Date" type="date" value={form.scheduled_date} onChange={(v) => setForm({ ...form, scheduled_date: v })} required testid="mon-date" />
            <button type="submit" data-testid="mon-submit" className="w-full bg-emerald-700 text-white py-2.5 rounded-lg font-medium">Schedule</button>
          </form>
        </Modal>
      )}
    </div>
  );
}


// =============== CHARTER VIEW (Part A + Part B) ===============
function CharterView({ charter, project, users, onSaved }) {
  const [c, setC] = useState({ ...charter });
  useEffect(() => { setC({ ...charter }); }, [charter]);
  const upd = (patch) => setC((s) => ({ ...s, ...patch }));
  const sd = c.stakeholder_directory || { client_side: [], pt_side: [] };

  const updateStakeholder = (side, i, field, value) => {
    const list = [...(sd[side] || [])];
    list[i] = { ...list[i], [field]: value };
    upd({ stakeholder_directory: { ...sd, [side]: list } });
  };

  const addStakeholder = (side) => {
    const list = [...(sd[side] || []), { role: "", name: "", phone: "", email: "" }];
    upd({ stakeholder_directory: { ...sd, [side]: list } });
  };

  const removeStakeholder = (side, i) => {
    const list = (sd[side] || []).filter((_, x) => x !== i);
    upd({ stakeholder_directory: { ...sd, [side]: list } });
  };

  const saveCharter = async () => {
    try {
      await api.put(`/projects/${project.id}/charter`, c);
      toast.success("Charter saved");
      onSaved();
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        {c.approval_status === "Approved" ? (
          <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full" data-testid="charter-approved-badge">
            <CheckCircle2 className="w-3.5 h-3.5" /> Client approved
            {c.approved_by_name ? ` by ${c.approved_by_name}` : ""}
            {c.approved_at ? ` on ${new Date(c.approved_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}` : ""}
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full" data-testid="charter-pending-badge">
            <Clock className="w-3.5 h-3.5" /> Awaiting client sign-off (via Client Portal)
          </span>
        )}
        <button onClick={saveCharter} data-testid="charter-save"
          className="flex items-center gap-2 bg-[#72BE44] hover:bg-[#5da636] text-white px-4 py-2 rounded-lg text-sm font-medium">
          <Save className="w-4 h-4" /> Save Charter
        </button>
      </div>

      {/* PART A */}
      <div className="bg-white border border-stone-200 rounded-xl p-6">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#72BE44] font-bold mb-1">Part A</div>
        <h3 className="text-lg font-bold text-[#0F2A4A] mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>Project Details</h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <Row label="Client Name" value={c.client_name} />
          <Row label="Project Name" value={c.project_name} />
          <Row label="Project Location" value={c.project_location} />
          <Row label="Project Type" value={c.project_type || project.service_type} />
          <Row label="Built-up Area" value={c.builtup_area || project.builtup_area || "—"} />
          <Row label="EC Category" value={c.ec_category || project.ec_category || "—"} />
          <Row label="Contract Value" value={fmtINR(c.contract_value)} />
          <Row label="Services Contracted" value={c.services_contracted || project.service_type} />
          <Row label="Contract Reference No." value={c.contract_reference_number || project.wo_number} />
          <Row label="Project ID" value={project.project_id_external || "—"} />
        </div>
      </div>

      {/* PART B – Stakeholder Directory */}
      <div className="bg-white border border-stone-200 rounded-xl p-6">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#72BE44] font-bold mb-1">Part B</div>
        <h3 className="text-lg font-bold text-[#0F2A4A] mb-4 flex items-center gap-2" style={{ fontFamily: "Poppins, sans-serif" }}>
          <UsersIcon className="w-5 h-5 text-[#72BE44]" /> Stakeholder Directory
        </h3>

        <StakeholderTable
          title="Client Side"
          rows={sd.client_side || []}
          onChange={(i, f, v) => updateStakeholder("client_side", i, f, v)}
          onAdd={() => addStakeholder("client_side")}
          onRemove={(i) => removeStakeholder("client_side", i)}
          testidPrefix="csd"
        />

        <div className="mt-6">
          <StakeholderTable
            title="PT Side"
            rows={sd.pt_side || []}
            onChange={(i, f, v) => updateStakeholder("pt_side", i, f, v)}
            onAdd={() => addStakeholder("pt_side")}
            onRemove={(i) => removeStakeholder("pt_side", i)}
            testidPrefix="psd"
            users={users}
          />
        </div>
      </div>

      {/* Communication Matrix */}
      <div className="bg-white border border-stone-200 rounded-xl p-6">
        <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 font-semibold mb-3">Communication Matrix</div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          {Object.entries(c.communication_matrix || {}).map(([k, v]) => (
            <Row key={k} label={k.replace(/_/g, " ")} value={v} />
          ))}
        </div>
      </div>
    </div>
  );
}

function StakeholderTable({ title, rows, onChange, onAdd, onRemove, testidPrefix, users }) {
  const teamUsers = React.useMemo(() => (users || []).filter((u) => u.role !== "client"), [users]);
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-stone-200 rounded">
          <thead className="bg-[#0F2A4A] text-white text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Role</th>
              <th className="text-left px-3 py-2 font-semibold">Name</th>
              <th className="text-left px-3 py-2 font-semibold">Phone</th>
              <th className="text-left px-3 py-2 font-semibold">Email</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-stone-100">
                <td className="p-1.5">
                  <input value={r.role || ""} onChange={(e) => onChange(i, "role", e.target.value)} data-testid={`${testidPrefix}-role-${i}`}
                    className="w-full px-2 py-1 bg-stone-50 border border-transparent rounded text-xs focus:bg-white focus:border-[#72BE44] focus:outline-none" />
                </td>
                <td className="p-1.5">
                  {users ? (
                    <select value={r.name || ""} onChange={(e) => { const u = users.find(x => x.name === e.target.value); onChange(i, "name", e.target.value); if (u) { onChange(i, "email", u.email || ""); onChange(i, "phone", u.phone || ""); } }} data-testid={`${testidPrefix}-name-${i}`}
                      className="w-full px-2 py-1 bg-stone-50 border border-transparent rounded text-xs focus:bg-white focus:border-[#72BE44] focus:outline-none">
                      <option value="">— Select —</option>
                      {teamUsers.map(u => <option key={u.id} value={u.name}>{u.name}</option>)}
                    </select>
                  ) : (
                    <input value={r.name || ""} onChange={(e) => onChange(i, "name", e.target.value)} data-testid={`${testidPrefix}-name-${i}`}
                      className="w-full px-2 py-1 bg-stone-50 border border-transparent rounded text-xs focus:bg-white focus:border-[#72BE44] focus:outline-none" />
                  )}
                </td>
                <td className="p-1.5">
                  <input value={r.phone || ""} onChange={(e) => onChange(i, "phone", e.target.value)} data-testid={`${testidPrefix}-phone-${i}`}
                    className="w-full px-2 py-1 bg-stone-50 border border-transparent rounded text-xs focus:bg-white focus:border-[#72BE44] focus:outline-none" />
                </td>
                <td className="p-1.5">
                  <input value={r.email || ""} onChange={(e) => onChange(i, "email", e.target.value)} type="email" data-testid={`${testidPrefix}-email-${i}`}
                    className="w-full px-2 py-1 bg-stone-50 border border-transparent rounded text-xs focus:bg-white focus:border-[#72BE44] focus:outline-none" />
                </td>
                <td className="p-1.5 text-right">
                  <button onClick={() => onRemove(i)} className="text-rose-500 hover:text-rose-700 text-xs">×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={onAdd} data-testid={`${testidPrefix}-add`} className="mt-2 text-xs text-[#72BE44] hover:underline font-medium">+ Add Contact</button>
    </div>
  );
}

// =============== TIMELINE VIEW (Milestone Engine) ===============
function TimelineView({ projectId, milestones, users, onReload }) {
  const [filter, setFilter] = useState("All");
  const [estimate, setEstimate] = useState(null);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const teamUsers = React.useMemo(() => users.filter(u => u.role !== "client"), [users]);
  const phases = [...new Set(milestones.map(m => m.phase))];

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/timeline-estimate`).then(r => setEstimate(r.data)).catch(() => setEstimate(null));
  }, [projectId, milestones.length]);

  const updateMilestone = async (mid, patch) => {
    try {
      await api.put(`/milestones/${mid}`, patch);
      onReload();
    } catch { toast.error("Update failed"); }
  };

  const regenerate = async () => {
    if (!confirmRegen) {
      setConfirmRegen(true);
      setTimeout(() => setConfirmRegen(false), 5000);
      return;
    }
    setConfirmRegen(false);
    try {
      const { data } = await api.post(`/projects/${projectId}/milestones/regenerate`);
      toast.success(`Timeline regenerated — ${data.count} milestones created`);
      onReload();
    } catch { toast.error("Failed"); }
  };

  const total = milestones.length;
  const completed = milestones.filter(m => m.status === "Completed").length;
  const delayed = milestones.filter(m => m.status === "Delayed").length;
  const progressPct = total ? Math.round((completed / total) * 100) : 0;

  const filtered = filter === "All" ? milestones : milestones.filter(m => m.phase === filter);

  return (
    <div className="space-y-5">
      {/* EC Estimate Summary Card */}
      {estimate && (
        <div className="bg-gradient-to-br from-[#0F2A4A] to-[#1e3a5f] text-white border border-[#0F2A4A] rounded-xl p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#72BE44] font-bold mb-3">EC Estimate Summary</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <EstCell label="Start Date" value={estimate.start_date || "—"} />
            <EstCell label="Estimated EC Date" value={estimate.estimated_ec_date || "—"} accent />
            <EstCell label="Target Completion" value={estimate.target_completion_date || "—"} />
            <EstCell label="Total Days" value={estimate.total_days} />
            <EstCell label="Days Remaining" value={estimate.days_remaining} warn={estimate.is_delayed} />
          </div>
        </div>
      )}

      <div className="bg-white border border-stone-200 rounded-xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-bold text-[#0F2A4A] flex items-center gap-2"><CalendarClock className="w-5 h-5 text-[#72BE44]" /> Project Timeline</h3>
            <p className="text-stone-500 text-sm mt-1">{total} milestones across {phases.length} phases · {completed} completed · {delayed > 0 && <span className="text-rose-600 font-semibold">{delayed} delayed</span>}</p>
          </div>
          <button onClick={regenerate} data-testid="timeline-regen"
            className={`text-xs px-3 py-1.5 rounded font-medium transition ${confirmRegen
              ? "bg-rose-600 text-white hover:bg-rose-700"
              : "bg-stone-100 hover:bg-stone-200 text-[#0F2A4A]"}`}>
            {confirmRegen ? "Click again to confirm — existing milestones will be replaced" : "Regenerate from Template"}
          </button>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-xs text-stone-600 mb-1.5">
            <span>Overall Progress</span><span className="font-semibold">{progressPct}%</span>
          </div>
          <div className="w-full h-2 bg-stone-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#72BE44] rounded-full transition-all" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter("All")} className={`text-xs px-3 py-1.5 rounded-full border ${filter === "All" ? "bg-[#0F2A4A] text-white border-[#0F2A4A]" : "bg-white border-stone-200 text-stone-600 hover:border-[#72BE44]"}`}>All Phases</button>
        {phases.map(ph => (
          <button key={ph} onClick={() => setFilter(ph)} data-testid={`timeline-phase-${ph}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${filter === ph ? "bg-[#0F2A4A] text-white border-[#0F2A4A]" : "bg-white border-stone-200 text-stone-600 hover:border-[#72BE44]"}`}>
            {ph}
          </button>
        ))}
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F2A4A] text-white text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2.5 font-semibold">Phase</th>
              <th className="text-left px-3 py-2.5 font-semibold">Milestone</th>
              <th className="text-left px-3 py-2.5 font-semibold">Target</th>
              <th className="text-left px-3 py-2.5 font-semibold">Actual</th>
              <th className="text-left px-3 py-2.5 font-semibold">Assignee</th>
              <th className="text-left px-3 py-2.5 font-semibold">Status</th>
              <th className="text-right px-3 py-2.5 font-semibold">Delay</th>
              <th className="text-center px-3 py-2.5 font-semibold">Client View</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.id} data-testid={`ms-row-${m.id}`} className="border-t border-stone-100 hover:bg-[#72BE44]/5">
                <td className="px-3 py-2 text-xs text-stone-500">{m.phase}</td>
                <td className="px-3 py-2 font-medium text-[#0F2A4A]">{m.name}</td>
                <td className="px-3 py-2 text-xs">
                  <input type="date" value={m.target_date || ""} onChange={(e) => updateMilestone(m.id, { target_date: e.target.value })} data-testid={`ms-target-${m.id}`}
                    className="px-2 py-1 bg-stone-50 border border-transparent rounded text-xs focus:bg-white focus:border-[#72BE44] focus:outline-none" />
                </td>
                <td className="px-3 py-2 text-xs">
                  <input type="date" value={m.actual_date || ""} onChange={(e) => updateMilestone(m.id, { actual_date: e.target.value })} data-testid={`ms-actual-${m.id}`}
                    className="px-2 py-1 bg-stone-50 border border-transparent rounded text-xs focus:bg-white focus:border-[#72BE44] focus:outline-none" />
                </td>
                <td className="px-3 py-2">
                  <select value={m.assignee || ""} onChange={(e) => updateMilestone(m.id, { assignee: e.target.value })} data-testid={`ms-assignee-${m.id}`}
                    className="px-2 py-1 bg-stone-50 border border-transparent rounded text-xs focus:bg-white focus:border-[#72BE44] focus:outline-none">
                    <option value="">—</option>
                    {teamUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <select value={m.status} onChange={(e) => updateMilestone(m.id, { status: e.target.value })} data-testid={`ms-status-${m.id}`}
                    className={`px-2 py-1 rounded text-[10px] font-semibold uppercase tracking-wider border-0 focus:outline-none focus:ring-2 focus:ring-[#72BE44] ${
                      m.status === "Completed" ? "bg-emerald-100 text-emerald-800"
                      : m.status === "In Progress" ? "bg-amber-100 text-amber-800"
                      : m.status === "Delayed" ? "bg-rose-100 text-rose-800"
                      : m.status === "Skipped" ? "bg-stone-200 text-stone-700"
                      : "bg-stone-100 text-stone-700"}`}>
                    <option>Pending</option><option>In Progress</option><option>Completed</option><option>Delayed</option><option>Skipped</option>
                  </select>
                </td>
                <td className="px-3 py-2 text-right text-xs">
                  {m.delay_days > 0 ? <span className="text-rose-600 font-semibold flex items-center justify-end gap-1"><AlertTriangle className="w-3 h-3" />{m.delay_days}d</span> : <span className="text-stone-400">—</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  <input type="checkbox" checked={!!m.client_visible} onChange={(e) => updateMilestone(m.id, { client_visible: e.target.checked })} data-testid={`ms-vis-${m.id}`} />
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center py-16 text-stone-400">
                <CalendarClock className="w-8 h-8 mx-auto mb-2 opacity-40" /> No milestones — click <b>Regenerate from Template</b> above
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EstCell({ label, value, accent, warn }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-300 font-semibold mb-1">{label}</div>
      <div className={`text-base font-bold ${accent ? "text-[#72BE44]" : warn ? "text-rose-300" : "text-white"}`}>{value}</div>
    </div>
  );
}

// ============ REQUIREMENTS VIEW (Sprint B) ============
function RequirementsView({ projectId }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [addOpen, setAddOpen] = useState(false);
  const [newReq, setNewReq] = useState({ name: "", category: "General", owner: "Client" });
  const load = () => api.get(`/projects/${projectId}/requirements`).then(r => setItems(r.data || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const update = async (rid, patch) => { try { await api.put(`/requirements/${rid}`, patch); load(); } catch { toast.error("Failed"); } };
  const add = async (e) => {
    e.preventDefault();
    if (!newReq.name.trim()) return;
    try {
      await api.post(`/projects/${projectId}/requirements`, { ...newReq, verification_status: "Awaited" });
      toast.success("Requirement added");
      setAddOpen(false);
      setNewReq({ name: "", category: "General", owner: "Client" });
      load();
    } catch { toast.error("Failed"); }
  };

  const categories = [...new Set(items.map(i => i.category))].filter(Boolean);
  const filtered = filter === "All" ? items : items.filter(i => i.category === filter);
  const stats = { total: items.length, awaited: items.filter(i => i.verification_status === "Awaited").length,
                  received: items.filter(i => i.verification_status === "Received").length,
                  verified: items.filter(i => i.verification_status === "Verified").length };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total" value={stats.total} color="#0F2A4A" />
        <StatCard label="Awaited" value={stats.awaited} color="#D97706" />
        <StatCard label="Received" value={stats.received} color="#0284C7" />
        <StatCard label="Verified" value={stats.verified} color="#72BE44" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setFilter("All")} className={`text-xs px-3 py-1.5 rounded-full border ${filter === "All" ? "bg-[#0F2A4A] text-white" : "bg-white border-stone-200 text-stone-600"}`}>All ({items.length})</button>
        {categories.map(c => (
          <button key={c} onClick={() => setFilter(c)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === c ? "bg-[#0F2A4A] text-white" : "bg-white border-stone-200 text-stone-600"}`}>{c}</button>
        ))}
        <button onClick={() => setAddOpen(true)} data-testid="req-add" className="ml-auto text-xs flex items-center gap-1 bg-[#72BE44] text-white px-3 py-1.5 rounded font-medium"><Plus className="w-3.5 h-3.5" /> Add Requirement</button>
      </div>
      {addOpen && (
        <Modal title="Add Requirement" onClose={() => setAddOpen(false)}>
          <form onSubmit={add} className="space-y-3">
            <Input label="Requirement Name" value={newReq.name} onChange={(v) => setNewReq({ ...newReq, name: v })} required testid="req-new-name" />
            <Input label="Category" value={newReq.category} onChange={(v) => setNewReq({ ...newReq, category: v })} testid="req-new-category" />
            <Select label="Owner" value={newReq.owner} onChange={(v) => setNewReq({ ...newReq, owner: v })} options={["Client", "Architect", "MEP", "PT", "NOC-Authority"]} testid="req-new-owner" />
            <button type="submit" data-testid="req-new-submit" className="w-full bg-emerald-700 text-white py-2.5 rounded-lg font-medium">Add Requirement</button>
          </form>
        </Modal>
      )}
      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F2A4A] text-white text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2.5 font-semibold">Requirement</th>
              <th className="text-left px-3 py-2.5 font-semibold">Category</th>
              <th className="text-left px-3 py-2.5 font-semibold">Owner</th>
              <th className="text-left px-3 py-2.5 font-semibold">Requested</th>
              <th className="text-left px-3 py-2.5 font-semibold">Submitted</th>
              <th className="text-right px-3 py-2.5 font-semibold">Pending Days</th>
              <th className="text-left px-3 py-2.5 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} data-testid={`req-row-${r.id}`} className="border-t border-stone-100 hover:bg-[#72BE44]/5">
                <td className="px-3 py-2 font-medium text-[#0F2A4A]">{r.name}</td>
                <td className="px-3 py-2 text-xs text-stone-600">{r.category}</td>
                <td className="px-3 py-2">
                  <select value={r.owner || ""} onChange={(e) => update(r.id, { owner: e.target.value })} data-testid={`req-owner-${r.id}`}
                    className="text-xs px-2 py-1 bg-stone-50 border border-transparent rounded focus:bg-white focus:border-[#72BE44] focus:outline-none">
                    {["Client", "Architect", "MEP", "PT", "NOC-Authority"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2 text-xs">
                  <input type="date" value={r.requested_date || ""} onChange={(e) => update(r.id, { requested_date: e.target.value })}
                    className="text-xs px-2 py-1 bg-stone-50 border-0 rounded" />
                </td>
                <td className="px-3 py-2 text-xs">
                  <input type="date" value={r.submitted_date || ""} onChange={(e) => update(r.id, { submitted_date: e.target.value })} data-testid={`req-submitted-${r.id}`}
                    className="text-xs px-2 py-1 bg-stone-50 border-0 rounded" />
                </td>
                <td className="px-3 py-2 text-right text-xs">
                  {r.pending_days > 7 ? <span className="text-rose-600 font-semibold">{r.pending_days}d</span>
                                       : <span className="text-stone-500">{r.pending_days}d</span>}
                </td>
                <td className="px-3 py-2">
                  <select value={r.verification_status} onChange={(e) => update(r.id, { verification_status: e.target.value })} data-testid={`req-status-${r.id}`}
                    className={`text-[10px] uppercase font-semibold rounded px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-[#72BE44] ${
                      r.verification_status === "Verified" ? "bg-emerald-100 text-emerald-800"
                      : r.verification_status === "Received" ? "bg-sky-100 text-sky-800"
                      : r.verification_status === "Rejected" ? "bg-rose-100 text-rose-800"
                      : "bg-amber-100 text-amber-800"}`}>
                    <option>Awaited</option><option>Received</option><option>Verified</option><option>Rejected</option>
                  </select>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-12 text-stone-400">No requirements in this category</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-4 border-l-4" style={{ borderLeftColor: color }}>
      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

// ============ 14 FOLDERS VIEW (Sprint B) ============
function FoldersView({ projectId }) {
  const [folders, setFolders] = useState([]);
  const [open, setOpen] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadFolders = () =>
    api.get(`/projects/${projectId}/folders`).then(r => setFolders(r.data || []));
  useEffect(() => { loadFolders(); }, [projectId]);

  const openFolder = async (f) => {
    setOpen(f);
    const { data } = await api.get(`/projects/${projectId}/folders/${f.code}/documents`);
    setFiles(data);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !open) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("File too large (max 8 MB)"); e.target.value = ""; return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post(`/projects/${projectId}/documents`, {
          project_id: projectId,
          name: file.name,
          category: open.name,      // folder name (e.g. "Client Documents")
          status: "Uploaded",
          file_url: reader.result,  // data URL
          folder_code: open.code,   // explicit — skips auto-heuristic
          source: "team",
        });
        toast.success(`${file.name} uploaded to ${open.name}`);
        await openFolder(open);
        await loadFolders();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed");
      } finally {
        setUploading(false);
        e.target.value = "";
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {folders.map(f => (
          <button key={f.id} onClick={() => openFolder(f)} data-testid={`folder-${f.code}`}
            className={`text-left bg-white border rounded-xl p-4 transition ${
              open?.code === f.code ? "border-[#72BE44] ring-2 ring-[#72BE44]/30" : "border-stone-200 hover:border-[#72BE44]"
            }`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#72BE44] font-bold">{f.code}</div>
                <div className="font-bold text-[#0F2A4A] mt-1">{f.name}</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-[#0F2A4A]">{f.file_count || 0}</div>
                <div className="text-[10px] text-stone-500 uppercase">files</div>
              </div>
            </div>
            {f.approved_count > 0 && <div className="text-[10px] text-emerald-700 mt-2">{f.approved_count} approved</div>}
          </button>
        ))}
      </div>
      {open && (
        <div className="bg-white border border-stone-200 rounded-xl p-5" data-testid="folder-panel">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#72BE44] font-bold">{open.code}</div>
              <h3 className="text-lg font-bold text-[#0F2A4A]">{open.name} ({files.length})</h3>
            </div>
            <div className="flex items-center gap-2">
              <label
                data-testid={`folder-upload-${open.code}`}
                className={`inline-flex items-center gap-1.5 bg-[#0F2A4A] hover:bg-[#0F2A4A]/90 text-white text-xs px-3 py-2 rounded-md cursor-pointer font-medium ${uploading ? "opacity-60 pointer-events-none" : ""}`}
              >
                <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading..." : "Upload File"}
                <input type="file" hidden onChange={handleUpload} data-testid={`folder-upload-input-${open.code}`} />
              </label>
              <button onClick={() => { setOpen(null); setFiles([]); }} className="text-xs text-stone-500 hover:text-rose-600">Close</button>
            </div>
          </div>
          <p className="text-[11px] text-stone-500 mb-3">
            Files uploaded here are stored under folder <b>{open.code} · {open.name}</b> and are also visible on the Checklist tab.
          </p>
          {files.length === 0 ? (
            <div className="text-sm text-stone-500 py-8 text-center border border-dashed border-stone-200 rounded-lg">
              No files yet — click <b>Upload File</b> to add the first document to this folder.
            </div>
          ) : (
            <div className="space-y-2">
              {files.map(d => (
                <div key={d.id} className="flex items-center gap-3 border border-stone-100 rounded-lg p-2.5" data-testid={`folder-file-${d.id}`}>
                  <div className={`w-2 h-2 rounded-full ${d.status === "Approved" ? "bg-emerald-600" : d.status === "Uploaded" ? "bg-sky-500" : "bg-amber-500"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-stone-800 truncate">{d.name}</div>
                    {d.source === "client" && <div className="text-[10px] text-[#72BE44] font-medium">Uploaded by client</div>}
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-stone-500">{d.status}</span>
                  {d.file_url && <a href={d.file_url} target="_blank" rel="noreferrer" className="text-stone-500 hover:text-[#0F2A4A]"><Download className="w-3.5 h-3.5" /></a>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
